import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bell, Moon, MessageSquare, Mail, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner } from "../components/common";

/* Spec §14 — Notifications preferences page. Consumes /api/notifications/prefs
   + /api/notifications/usage. Renders quiet hours, per-channel toggles, per-category
   × channel matrix, and the SMS/WhatsApp allowance meter. */

const CHANNELS = [
  { id: "in_app",   label: "In-app", Icon: Bell },
  { id: "push",     label: "Push",   Icon: Smartphone },
  { id: "email",    label: "Email",  Icon: Mail },
  { id: "sms",      label: "SMS",    Icon: MessageSquare, cost: true },
  { id: "whatsapp", label: "WhatsApp", Icon: MessageSquare, cost: true },
];

const CATEGORIES = [
  { id: "itinerary_changes",   label: "Itinerary changes" },
  { id: "check_in_reminders",  label: "Check-in reminders" },
  { id: "sos",                 label: "SOS alerts", critical: true },
  { id: "guardian_alerts",     label: "Guardian alerts", critical: true },
  { id: "group_announcements", label: "Group announcements" },
  { id: "marketing",           label: "Marketing" },
];

export default function NotificationPreferences() {
  const { user } = useAuth() || {};
  const [prefs, setPrefs] = useState(null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    Promise.all([
      api.get("/notifications/prefs").then((r) => setPrefs(r.data)),
      api.get("/notifications/usage").then((r) => setUsage(r.data)),
    ]).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/notifications/prefs", patch);
      setPrefs(data);
      toast.success("Saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const toggleChannel = (ch) => save({ channels: { [ch]: { enabled: !prefs.channels[ch]?.enabled } } });
  const toggleCategory = (cat, ch) => save({
    categories: { [cat]: { [ch]: !(prefs.categories[cat] || {})[ch] } },
  });
  const patchQH = (patch) => save({ quiet_hours: { ...prefs.quiet_hours, ...patch } });

  if (!user) return <div className="max-w-lg mx-auto p-8 text-center"><div className="font-serif font-800 text-2xl">Sign in to manage notifications</div><Link to="/login" className="inline-block mt-4 bg-blue-600 text-white px-5 py-2.5 rounded-full font-700">Sign in</Link></div>;
  if (!prefs) return <Spinner />;

  return (
    <div className="max-w-3xl mx-auto px-5 py-8" data-testid="notif-prefs-page">
      <Link to="/travel-hub" className="inline-flex items-center gap-1 text-blue-700 text-sm hover:underline mb-3">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>
      <h1 className="font-serif font-800 text-4xl text-slate-900">Notifications</h1>
      <p className="text-slate-600 mt-2">Control which alerts reach you, when, and how.</p>

      {/* Channels */}
      <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] font-800 uppercase tracking-widest text-slate-500 mb-3">Channels</div>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {CHANNELS.map(({ id, label, Icon, cost }) => (
            <label key={id} className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 cursor-pointer hover:bg-slate-50">
              <span className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 grid place-items-center"><Icon className="w-4.5 h-4.5" /></span>
              <span className="flex-1">
                <div className="font-700 text-slate-800">{label}</div>
                {cost && <div className="text-[10px] text-amber-700 uppercase tracking-widest">Cost-bearing</div>}
              </span>
              <input type="checkbox" data-testid={`ch-${id}`}
                checked={!!prefs.channels[id]?.enabled} onChange={() => toggleChannel(id)} disabled={busy}
                className="w-11 h-6 rounded-full appearance-none bg-slate-300 checked:bg-emerald-500 relative cursor-pointer transition-colors before:absolute before:content-[''] before:w-5 before:h-5 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-5" />
            </label>
          ))}
        </div>
      </section>

      {/* Quiet Hours */}
      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Moon className="w-4 h-4 text-indigo-600" />
          <div className="text-[11px] font-800 uppercase tracking-widest text-slate-500">Quiet Hours</div>
        </div>
        <label className="flex items-center justify-between py-2">
          <span className="text-slate-800">Enable quiet hours</span>
          <input type="checkbox" data-testid="qh-enabled" checked={!!prefs.quiet_hours.enabled}
            onChange={(e) => patchQH({ enabled: e.target.checked })}
            className="w-11 h-6 rounded-full appearance-none bg-slate-300 checked:bg-emerald-500 relative cursor-pointer transition-colors before:absolute before:content-[''] before:w-5 before:h-5 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-5" />
        </label>
        {prefs.quiet_hours.enabled && (
          <div className="grid grid-cols-2 gap-3 mt-2">
            <label className="block"><div className="text-xs text-slate-500 mb-1">Start</div>
              <input type="time" data-testid="qh-start" value={prefs.quiet_hours.start}
                onChange={(e) => patchQH({ start: e.target.value })}
                className="w-full rounded-xl border border-slate-200 py-2 px-3 outline-none focus:border-blue-500" /></label>
            <label className="block"><div className="text-xs text-slate-500 mb-1">End</div>
              <input type="time" data-testid="qh-end" value={prefs.quiet_hours.end}
                onChange={(e) => patchQH({ end: e.target.value })}
                className="w-full rounded-xl border border-slate-200 py-2 px-3 outline-none focus:border-blue-500" /></label>
            <label className="col-span-2 flex items-center justify-between py-1">
              <span className="text-sm text-slate-700">Let SOS &amp; Guardian alerts through anyway</span>
              <input type="checkbox" data-testid="qh-critical" checked={!!prefs.quiet_hours.critical_override}
                onChange={(e) => patchQH({ critical_override: e.target.checked })}
                className="w-11 h-6 rounded-full appearance-none bg-slate-300 checked:bg-emerald-500 relative cursor-pointer before:absolute before:content-[''] before:w-5 before:h-5 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-5" />
            </label>
          </div>
        )}
      </section>

      {/* Category × Channel matrix */}
      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-[11px] font-800 uppercase tracking-widest text-slate-500 mb-3">Category × Channel</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] font-800 uppercase tracking-wider text-slate-500">
              <th className="text-left py-2 font-800">Category</th>
              {CHANNELS.map((c) => <th key={c.id} className="text-center px-1 py-2 font-800">{c.label}</th>)}
            </tr></thead>
            <tbody>{CATEGORIES.map((cat) => (
              <tr key={cat.id} className="border-t border-slate-100">
                <td className="py-2 text-slate-800 font-600">{cat.label}
                  {cat.critical && <span className="ml-1.5 text-[9px] font-800 uppercase tracking-wider bg-rose-100 text-rose-700 rounded-full px-1.5 py-0.5">Critical</span>}
                </td>
                {CHANNELS.map((ch) => (
                  <td key={ch.id} className="text-center px-1">
                    <input type="checkbox" data-testid={`cat-${cat.id}-${ch.id}`}
                      checked={!!(prefs.categories[cat.id] || {})[ch.id]}
                      onChange={() => toggleCategory(cat.id, ch.id)} disabled={busy}
                      className="w-4 h-4 rounded accent-blue-600 cursor-pointer" />
                  </td>
                ))}
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      {/* Usage */}
      {usage && (
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="notif-usage">
          <div className="text-[11px] font-800 uppercase tracking-widest text-slate-500 mb-3">This month · {usage.month}</div>
          {["sms", "whatsapp"].map((ch) => (
            <div key={ch} className="mb-3 last:mb-0">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-700 text-slate-800 capitalize">{ch}</span>
                <span className="text-slate-500">{usage.counts[ch]} / {usage.allowance[ch]}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${usage.counts[ch] >= usage.allowance[ch] ? "bg-rose-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, (usage.counts[ch] / usage.allowance[ch]) * 100)}%` }} />
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
